import type { BillingAddress } from '@/types/payment'
import type { ResidentApplicationData } from '@/types/application'
import type { StreetType } from '@/lib/api/streetsService'
import type { PlaceResult } from '@/hooks/useGooglePlacesAutocomplete'
import { parseAddressComponents } from '@/lib/utils/addressParser'
import type { PaymentFormAutoFilled } from './paymentFormTypes'

type StateOption = { state_code: string; state_name: string }

export function normalizeStateCode(
  state: string | undefined,
  states: StateOption[],
): string {
  const stateCode = state ? state.trim().toUpperCase() : ''
  if (!stateCode || states.length === 0) return stateCode

  const stateExists = states.some((s) => s.state_code === stateCode)
  if (stateExists) return stateCode

  const matchedState = states.find((s) => s.state_code.toUpperCase() === stateCode)
  return matchedState?.state_code ?? stateCode
}

export function buildBillingFromCurrentAddress(
  addr: NonNullable<ResidentApplicationData['current_address']>,
  states: StateOption[],
): BillingAddress {
  let addressType: string = 'STREET'
  if (
    addr.street &&
    (addr.street.toUpperCase().includes('PO BOX') ||
      addr.street.toUpperCase().includes('P.O. BOX') ||
      addr.street.toUpperCase().includes('POST OFFICE BOX'))
  ) {
    addressType = 'POBOX'
  } else if (addr.address_type) {
    addressType = addr.address_type === 'PO_BOX' ? 'POBOX' : addr.address_type
  }

  return {
    house_no: addr.house_no || '',
    street: addr.street || '',
    apartment: addr.apartment || undefined,
    city: addr.city || '',
    state: normalizeStateCode(addr.state, states),
    zip_code: addr.zip_code || '',
    address_type: addressType,
  }
}

export function autoFilledFromBilling(billing: BillingAddress): PaymentFormAutoFilled {
  return {
    firstName: false,
    lastName: false,
    houseNo: !!billing.house_no,
    street: !!billing.street,
    apartment: !!billing.apartment,
    city: !!billing.city,
    state: !!billing.state,
    zipCode: !!billing.zip_code,
  }
}

export function applyPlaceToBillingAddress(
  place: PlaceResult,
  prev: BillingAddress,
  streetTypes: StreetType[],
): BillingAddress {
  const parsed = parseAddressComponents(place)
  const newAddress = { ...prev }
  if (parsed.formattedAddress) newAddress.payment_address = parsed.formattedAddress
  if (parsed.streetNumber) newAddress.house_no = parsed.streetNumber
  if (parsed.streetName) newAddress.street = parsed.streetName
  if (parsed.city) newAddress.city = parsed.city
  if (parsed.state) newAddress.state = parsed.state
  if (parsed.zipCode) newAddress.zip_code = parsed.zipCode

  if (parsed.streetName && streetTypes.length > 0) {
    streetTypes.forEach((st) => {
      const sNameLower = parsed.streetName!.toLowerCase()
      if (sNameLower.endsWith(` ${st.street_suffix.toLowerCase()}`)) {
        newAddress.street_type = st.street_suffix_code
        newAddress.street = parsed
          .streetName!.substring(0, parsed.streetName!.length - st.street_suffix.length - 1)
          .trim()
      } else if (sNameLower.endsWith(` ${st.street_suffix_code.toLowerCase()}`)) {
        newAddress.street_type = st.street_suffix_code
        newAddress.street = parsed
          .streetName!.substring(0, parsed.streetName!.length - st.street_suffix_code.length - 1)
          .trim()
      }
    })
  }
  return newAddress
}

export const EMPTY_BILLING_ADDRESS: BillingAddress = {
  house_no: '',
  street: '',
  apartment: undefined,
  city: '',
  state: '',
  zip_code: '',
  address_type: 'STREET',
}
